import React, { useState, useEffect, useRef } from 'react';
import client from '../../api/client';

const STATUS_ORDER = ['open', 'spare_dispatched', 'spare_delivered', 'faulty_picked_up', 'oem_rma_raised', 'closed'];
const OEM_STATUS_ORDER = ['open', 'faulty_picked_up', 'oem_rma_raised', 'closed'];
const STATUS_LABEL = {
  open: 'Open',
  spare_dispatched: 'Spare Dispatched',
  spare_delivered: 'Spare Delivered',
  faulty_picked_up: 'Faulty Picked Up',
  oem_rma_raised: 'OEM RMA Raised',
  closed: 'Closed',
};
const STATUS_COLOR = {
  open: 'bg-blue-100 text-blue-700',
  spare_dispatched: 'bg-amber-100 text-amber-700',
  spare_delivered: 'bg-cyan-100 text-cyan-700',
  faulty_picked_up: 'bg-orange-100 text-orange-700',
  oem_rma_raised: 'bg-purple-100 text-purple-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

const inp = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300';
const label = (t) => <p className="text-xs font-semibold text-brand-400 uppercase mb-1">{t}</p>;

function Field({ title, value }) {
  if (!value) return null;
  return (
    <div>
      {label(title)}
      <p className="text-sm font-medium text-gray-800">{value}</p>
    </div>
  );
}

function ChallanLink({ url, title }) {
  if (!url) return null;
  return (
    <div>
      {label(title)}
      <a href={url} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-brand-500 hover:underline font-medium">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        View Challan
      </a>
    </div>
  );
}

export default function RMAPanel({ ticket, rmaChild: initialRma, onUpdate }) {
  const [rma, setRma] = useState(initialRma);
  const [expanded, setExpanded] = useState(true);
  const [activeForm, setActiveForm] = useState(null);
  const [form, setForm] = useState({});
  const [spareUnits, setSpareUnits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const challanRef = useRef(null);
  const returnChallanRef = useRef(null);
  const editChallanRef = useRef(null);
  // "RMA Created On" belongs to the ticket's own fields; it is kept here so the card shows a saved change at once
  const [createdOn, setCreatedOn] = useState(ticket?.fields?.rma_created_on || '');
  useEffect(() => { setCreatedOn(ticket?.fields?.rma_created_on || ''); }, [ticket?.fields?.rma_created_on]);

  useEffect(() => { setRma(initialRma); }, [initialRma]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openForm = async (stage) => {
    setError('');
    setActiveForm(stage);
    if (stage === 'create') {
      const dev = ticket?.device_detail || {};
      const loc = dev?.room?.location || {};
      setForm({
        faulty_serial_number: dev.serial_number || '',
        part_code: dev.model_part || '',
        part_description: dev.device_name || '',
        site_contact_name: loc.spoc_name || '',
        site_address: loc.address || '',
        site_pincode: loc.pincode || '',
        site_gstin: loc.gstin || '',
      });
      return;
    }
    if (stage === 'edit_details') {
      setForm({
        part_code: rma.part_code || '',
        part_description: rma.part_description || '',
        faulty_serial_number: rma.faulty_serial_number || '',
        site_contact_name: rma.site_contact_name || '',
        site_address: rma.site_address || '',
        site_pincode: rma.site_pincode || '',
        site_gstin: rma.site_gstin || '',
        oem_rma_number: rma.oem_rma_number || '',
      });
      return;
    }
    setForm({});
    if (stage === 'spare_dispatched') {
      // Load the spare reserved for THIS ticket (pre-selected) + all available spares
      try {
        const [availRes, resvRes] = await Promise.all([
          client.get('/inventory/spare-units/', { params: { status: 'available', page_size: 500 } }),
          client.get('/inventory/spare-units/', { params: { status: 'reserved', page_size: 500 } }),
        ]);
        const available = availRes.data.results || availRes.data || [];
        const reserved  = resvRes.data.results  || resvRes.data  || [];
        const linkedId  = rma?.spare_unit?.id ?? rma?.spare_unit ?? null;
        const mine = reserved
          .filter(u =>
            (linkedId && String(u.id) === String(linkedId)) ||
            String(u.used_in_ticket?.id ?? u.used_in_ticket ?? '') === String(ticket?.id)
          )
          .map(u => ({ ...u, _reservedForThis: true }));
        const mineIds = new Set(mine.map(u => String(u.id)));
        const others  = available.filter(u => u.status === 'available' && !mineIds.has(String(u.id)));
        setSpareUnits([...mine, ...others]);
        const defaultId = linkedId || mine[0]?.id;
        if (defaultId) setForm({ spare_unit: String(defaultId) });
      } catch { setSpareUnits([]); }
    }
  };

  const cancel = () => { setActiveForm(null); setForm({}); setError(''); };

  // ── Edit details on the finished steps: Faulty Pickup, OEM RMA, Replacement Received ──
  const EDIT_DATE_KEYS = ['faulty_pickup_date', 'oem_rma_date', 'replacement_received_date'];
  const openEdit = (stage) => {
    setError('');
    setActiveForm(stage);
    if (stage === 'edit_pickup') {
      setForm({
        faulty_pickup_date: rma.faulty_pickup_date || '',
        return_courier_type: rma.return_courier_type || '',
        return_courier_name: rma.return_courier_name || '',
        return_tracking_number: rma.return_tracking_number || '',
      });
    } else if (stage === 'edit_oem') {
      setForm({ oem_sr_number: rma.oem_sr_number || '', oem_rma_number: rma.oem_rma_number || '', oem_rma_date: rma.oem_rma_date || '' });
    } else if (stage === 'edit_replacement') {
      setForm({ replacement_serial_number: rma.replacement_serial_number || '', replacement_received_date: rma.replacement_received_date || '' });
    }
  };
  const saveEdit = async () => {
    setError('');
    const need = {
      edit_pickup: [['faulty_pickup_date', 'Pickup Date'], ['return_courier_type', 'Courier Type']],
      edit_oem: [['oem_rma_number', 'RMA Number'], ['oem_rma_date', 'RMA Date']],
      edit_replacement: [['replacement_serial_number', 'Replacement Serial Number'], ['replacement_received_date', 'Received Date']],
    }[activeForm] || [];
    const missing = need.filter(([k]) => !String(form[k] || '').trim()).map(([, t]) => t);
    if (missing.length) {
      setError(`${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} required.`);
      return;
    }
    const payload = { ...form };
    if (activeForm === 'edit_pickup' && payload.return_courier_type === 'hand') payload.return_tracking_number = '';
    const file = activeForm === 'edit_pickup' ? editChallanRef.current?.files?.[0] : null;
    setSaving(true);
    try {
      let body = payload;
      let cfg = {};
      if (file) {
        body = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== undefined && v !== null && !(v === '' && EDIT_DATE_KEYS.includes(k))) body.append(k, v);
        });
        body.append('return_challan', file);
        cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      }
      const resp = await client.patch(`/inventory/rma-tickets/${rma.id}/`, body, cfg);
      setRma(resp.data);
      setActiveForm(null);
      setForm({});
      if (onUpdate) onUpdate(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join(' ') || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };
  // Save of the RMA Details edit form: RMA Created On belongs to the ticket, so it is saved there first, then the RMA fields
  const saveDetailsAll = async () => {
    const wanted = form.rma_created_on;
    if (wanted && wanted !== createdOn) {
      setSaving(true);
      setError('');
      try {
        await client.patch(`/tickets/${ticket.id}/`, { fields: { rma_created_on: wanted } });
        setCreatedOn(wanted);
      } catch (e) {
        setError(e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join(' ') || 'Could not save RMA Created On.');
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    await saveDetails();
  };
  const editButtons = (
    <div className="flex gap-2 justify-end">
      <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
      <button onClick={saveEdit} disabled={saving}
        className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );

  const submit = async (urlPath, isMultipart = false) => {
    setSaving(true);
    setError('');
    try {
      let payload;
      if (isMultipart) {
        payload = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v !== undefined && v !== null) payload.append(k, v); });
        if (challanRef.current?.files[0]) payload.append('delivery_challan', challanRef.current.files[0]);
        if (returnChallanRef.current?.files[0]) payload.append('return_challan', returnChallanRef.current.files[0]);
      } else {
        payload = form;
      }
      const resp = await client.post(
        `/inventory/rma-tickets/${rma.id}/${urlPath}/`,
        payload,
        isMultipart ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}
      );
      setRma(resp.data);
      setActiveForm(null);
      setForm({});
      if (onUpdate) onUpdate(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const createRma = async () => {
    setSaving(true);
    setError('');
    try {
      const resp = await client.post('/inventory/rma-tickets/', {
        ...form,
        parent_ticket: ticket.id,
      });
      setRma(resp.data);
      setActiveForm(null);
      setForm({});
      if (onUpdate) onUpdate(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join(' ') || 'Failed to create.');
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = async () => {
    setSaving(true);
    setError('');
    try {
      const resp = await client.patch(`/inventory/rma-tickets/${rma.id}/`, form);
      setRma(resp.data);
      setActiveForm(null);
      setForm({});
      if (onUpdate) onUpdate(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join(' ') || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };
  const isFromOEM = !!(rma && !rma.spare_unit && !(rma.part_description || '').includes('spare borrowed from another room'));
  const activeOrder = isFromOEM ? OEM_STATUS_ORDER : STATUS_ORDER;
  const stepIndex = rma ? activeOrder.indexOf(rma.status) : -1;
  const posOf = (statusName) => activeOrder.indexOf(statusName);
  const canCreate = !rma && ['RMA', 'Replacement', 'Resolved', 'Recovery', 'Closed'].includes(ticket?.status);

  return (
    <div className="card p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="section-title mb-0">RMA Process</h3>
          {rma && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${STATUS_COLOR[rma.status]}`}>
              {STATUS_LABEL[rma.status]}
            </span>
          )}
          {rma && (
            <span className="text-xs font-mono text-gray-400">{rma.ticket_number}</span>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} className="text-gray-400 hover:text-gray-600">
          <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {expanded && (
        <>
          {/* Progress stepper */}
          {rma && (
            <div className="flex items-center gap-1 mb-5 overflow-x-auto pb-1">
              {activeOrder.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex-shrink-0 flex flex-col items-center gap-1`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                      i < stepIndex ? 'bg-emerald-500 border-emerald-500 text-white' :
                      i === stepIndex ? 'bg-brand-500 border-brand-500 text-white' :
                      'bg-white border-gray-200 text-gray-400'
                    }`}>
                      {i < stepIndex ? '✓' : i + 1}
                    </div>
                    <span className={`text-[10px] text-center leading-tight max-w-[60px] ${i <= stepIndex ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                      {STATUS_LABEL[s]}
                    </span>
                  </div>
                  {i < activeOrder.length - 1 && (
                    <div className={`flex-1 h-0.5 mt-[-12px] min-w-[16px] ${i < stepIndex ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          {/* No RMA yet — create */}
          {!rma && canCreate && activeForm !== 'create' && (
            <button onClick={() => { openForm('create'); }}
              className="w-full py-3 rounded-xl border-2 border-dashed border-brand-200 text-brand-500 text-sm font-semibold hover:border-brand-400 hover:bg-brand-50 transition-colors">
              + Create RMA Child Ticket
            </button>
          )}

          {/* Create form */}
          {activeForm === 'create' && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-brand-500 uppercase">Step 1 — RMA Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {label('Part Code')}
                  <input className={inp} value={form.part_code || ''} onChange={e => set('part_code', e.target.value)} placeholder="e.g. DELL-OPTI-3090" />
                </div>
                <div>
                  {label('Part Description')}
                  <input className={inp} value={form.part_description || ''} onChange={e => set('part_description', e.target.value)} placeholder="Dell Mini PC OptiPlex 3090" />
                </div>
                <div>
                  {label('Faulty Serial Number')}
                  <input className={inp} value={form.faulty_serial_number || ''} onChange={e => set('faulty_serial_number', e.target.value)}
                    placeholder={ticket?.device_detail?.serial_number || ''} />
                </div>
                <div>
                  {label('Site Contact Person')}
                  <input className={inp} value={form.site_contact_name || ''} onChange={e => set('site_contact_name', e.target.value)} />
                </div>
                <div className="col-span-2">
                  {label('Site Address')}
                  <textarea className={inp} rows={2} value={form.site_address || ''} onChange={e => set('site_address', e.target.value)} />
                </div>
                <div>
                  {label('Pincode')}
                  <input className={inp} value={form.site_pincode || ''} onChange={e => set('site_pincode', e.target.value)} />
                </div>
                <div>
                  {label('GSTIN')}
                  <input className={inp} value={form.site_gstin || ''} onChange={e => set('site_gstin', e.target.value)} />
                </div>
                <div className="col-span-2">
                  {label('Notes')}
                  <textarea className={inp} rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={createRma} disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                  {saving ? 'Creating…' : 'Create RMA Ticket'}
                </button>
              </div>
            </div>
          )}

          {/* Existing RMA — show details + action forms */}
          {rma && (
            <div className="space-y-4">

              {/* Step 1 details */}
              <StepCard step={1} title="RMA Details" done={stepIndex >= 0}
                actionLabel={activeForm === 'edit_details' ? null : 'Edit'}
                onAction={() => openForm('edit_details')}>
                {activeForm === 'edit_details' ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {label('Part Code')}
                        <input className={inp} value={form.part_code || ''} onChange={e => set('part_code', e.target.value)} />
                      </div>
                      <div>
                        {label('Part Description')}
                        <input className={inp} value={form.part_description || ''} onChange={e => set('part_description', e.target.value)} />
                      </div>
                      <div>
                        {label('Faulty Serial Number')}
                        <input className={inp} value={form.faulty_serial_number || ''} onChange={e => set('faulty_serial_number', e.target.value)} />
                      </div>
                      <div>
                        {label('Site Contact Person')}
                        <input className={inp} value={form.site_contact_name || ''} onChange={e => set('site_contact_name', e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        {label('Site Address')}
                        <textarea className={inp} rows={2} value={form.site_address || ''} onChange={e => set('site_address', e.target.value)} />
                      </div>
                      <div>
                        {label('Pincode')}
                        <input className={inp} value={form.site_pincode || ''} onChange={e => set('site_pincode', e.target.value)} />
                      </div>
                      <div>
                        {label('GSTIN')}
                        <input className={inp} value={form.site_gstin || ''} onChange={e => set('site_gstin', e.target.value)} />
                      </div>
                      <div>
                        {label('RMA Number')}
                        <input className={inp} value={form.oem_rma_number || ''} onChange={e => set('oem_rma_number', e.target.value)} />
                      </div>
                      <div>
                        {label('RMA Created On')}
                        <input type="date" className={inp} value={form.rma_created_on !== undefined ? form.rma_created_on : (createdOn || '')} onChange={e => set('rma_created_on', e.target.value)} />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                      <button onClick={saveDetailsAll} disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <Field title="Part Code" value={rma.part_code} />
                    <Field title="Part Description" value={rma.part_description} />
                    <Field title="Faulty Serial No." value={rma.faulty_serial_number} />
                    <Field title="Site Contact" value={rma.site_contact_name} />
                    {rma.site_address && <div className="col-span-2"><Field title="Site Address" value={`${rma.site_address}${rma.site_pincode ? ', ' + rma.site_pincode : ''}`} /></div>}
                    <Field title="GSTIN" value={rma.site_gstin} />
                    <Field title="RMA Number" value={rma.oem_rma_number} />
                    <Field title="RMA Created On" value={createdOn} />
                  </div>
                )}
              </StepCard>

              {/* Step 2 — Dispatch */}
              {!isFromOEM && (
              <StepCard step={2} title="Spare Dispatch" done={stepIndex >= 1} active={stepIndex === 0}
                actionLabel={stepIndex === 0 ? 'Fill Dispatch Details' : null}
                onAction={() => openForm('spare_dispatched')}>
                {stepIndex >= 1 && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field title="Spare Serial" value={rma.spare_unit_detail?.serial_number} />
                    <Field title="Dispatch Date" value={rma.dispatch_date} />
                    <Field title="Courier Type" value={rma.courier_type ? (rma.courier_type === 'hand' ? 'By Hand' : 'Courier') : null} />
                    {rma.courier_name && <Field title="Courier / Person" value={rma.courier_name} />}
                    {rma.tracking_number && <Field title="Tracking Number" value={rma.tracking_number} />}
                    <Field title="Expected Delivery" value={rma.expected_delivery_date} />
                    <ChallanLink url={rma.delivery_challan_url} title="Delivery Challan" />
                  </div>
                )}
                {activeForm === 'spare_dispatched' && (
                  <DispatchForm form={form} set={set} spareUnits={spareUnits} challanRef={challanRef}
                    saving={saving} onCancel={cancel} onSubmit={() => submit('mark-dispatched', true)} />
                )}
              </StepCard>
              )}

              {/* Step 3 — Spare Delivered (auto) */}
              {!isFromOEM && (
              <StepCard step={3} title="Spare Delivered" done={stepIndex >= 2} active={stepIndex === 1}
                actionLabel={stepIndex === 1 ? 'Confirm Delivery' : null}
                onAction={() => openForm('spare_delivered')}>
                {stepIndex >= 2 && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field title="Actual Delivery Date" value={rma.actual_delivery_date} />
                    <Field title="Faulty → Warehouse" value={rma.faulty_spare_unit_detail?.serial_number ? `${rma.faulty_spare_unit_detail.serial_number} (Faulty)` : null} />
                    <div className="col-span-2">
                      <p className="text-xs text-emerald-600 font-medium">Spare serial installed in room inventory. Faulty device moved to warehouse stock.</p>
                    </div>
                  </div>
                )}
                {activeForm === 'spare_delivered' && (
                  <div className="mt-3 space-y-3">
                    <div>
                      {label('Actual Delivery Date *')}
                      <input type="date" className={inp} value={form.actual_delivery_date || ''} onChange={e => set('actual_delivery_date', e.target.value)} />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                      <button onClick={() => submit('mark-delivered')} disabled={saving}
                        className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                        {saving ? 'Saving…' : 'Confirm Delivery'}
                      </button>
                    </div>
                  </div>
                )}
              </StepCard>
              )}

              {/* Step 4 — Faulty Pickup */}
              <StepCard step={isFromOEM ? 2 : 4} title="Faulty Pickup" onEdit={rma.faulty_pickup_date && activeForm !== 'edit_pickup' ? () => openEdit('edit_pickup') : undefined} done={stepIndex >= posOf('faulty_picked_up')} active={stepIndex === posOf('faulty_picked_up') - 1}
                actionLabel={stepIndex === posOf('faulty_picked_up') - 1 ? 'Enter Pickup Details' : null}
                onAction={() => openForm('faulty_picked_up')}>
                {stepIndex >= posOf('faulty_picked_up') && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field title="Pickup Date" value={rma.faulty_pickup_date} />
                    <Field title="Courier Type" value={rma.return_courier_type ? (rma.return_courier_type === 'hand' ? 'By Hand' : 'Courier') : null} />
                    {rma.return_courier_name && <Field title="Courier / Person" value={rma.return_courier_name} />}
                    {rma.return_tracking_number && <Field title="Tracking Number" value={rma.return_tracking_number} />}
                    <ChallanLink url={rma.return_challan_url} title="Return Challan" />
                  </div>
                )}
                {activeForm === 'faulty_picked_up' && (
                  <FaultyPickupForm form={form} set={set} challanRef={returnChallanRef}
                    saving={saving} onCancel={cancel} onSubmit={() => submit('mark-faulty-picked-up', true)} />
                )}
                {/* edit details */}
                {activeForm === 'edit_pickup' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {label('Pickup Date *')}
                        <input type="date" className={inp} value={form.faulty_pickup_date || ''} onChange={e => set('faulty_pickup_date', e.target.value)} />
                      </div>
                      <div>
                        {label('Courier Type *')}
                        <select className={inp} value={form.return_courier_type || ''} onChange={e => set('return_courier_type', e.target.value)}>
                          <option value="">Select...</option>
                          <option value="courier">Courier</option>
                          <option value="hand">By Hand</option>
                        </select>
                      </div>
                      {form.return_courier_type === 'courier' && (
                        <>
                          <div>
                            {label('Courier Name')}
                            <input className={inp} value={form.return_courier_name || ''} onChange={e => set('return_courier_name', e.target.value)} />
                          </div>
                          <div>
                            {label('Tracking Number')}
                            <input className={inp} value={form.return_tracking_number || ''} onChange={e => set('return_tracking_number', e.target.value)} />
                          </div>
                        </>
                      )}
                      {form.return_courier_type === 'hand' && (
                        <div>
                          {label('Picked Up By')}
                          <input className={inp} value={form.return_courier_name || ''} onChange={e => set('return_courier_name', e.target.value)} />
                        </div>
                      )}
                      <div className="col-span-2">
                        {label('Return Challan (choose a file only to replace the current one)')}
                        <input type="file" ref={editChallanRef} accept=".pdf,.jpg,.jpeg,.png" className="text-sm text-gray-600" />
                      </div>
                    </div>
                    {editButtons}
                  </div>
                )}
              </StepCard>

              {/* Step 5 — OEM RMA */}
              <StepCard step={isFromOEM ? 3 : 5} title="OEM RMA" onEdit={rma.oem_rma_number && activeForm !== 'edit_oem' ? () => openEdit('edit_oem') : undefined} done={stepIndex >= posOf('oem_rma_raised')} active={stepIndex === posOf('oem_rma_raised') - 1}
                actionLabel={stepIndex === posOf('oem_rma_raised') - 1 ? 'Raise OEM RMA' : null}
                onAction={() => openForm('oem_rma_raised')}>
                {stepIndex >= posOf('oem_rma_raised') && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field title="SR Number" value={rma.oem_sr_number} />
                    <Field title="RMA Number" value={rma.oem_rma_number} />
                    <Field title="RMA Date" value={rma.oem_rma_date} />
                  </div>
                )}
                {activeForm === 'oem_rma_raised' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {label('SR Number')}
                        <input className={inp} value={form.oem_sr_number || ''} onChange={e => set('oem_sr_number', e.target.value)} />
                      </div>
                      <div>
                        {label('RMA Number *')}
                        <input className={inp} value={form.oem_rma_number || ''} onChange={e => set('oem_rma_number', e.target.value)} />
                      </div>
                      <div>
                        {label('RMA Date *')}
                        <input type="date" className={inp} value={form.oem_rma_date || ''} onChange={e => set('oem_rma_date', e.target.value)} />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                      <button onClick={() => submit('raise-oem-rma')} disabled={saving}
                        className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                        {saving ? 'Saving…' : 'Raise OEM RMA'}
                      </button>
                    </div>
                  </div>
                )}
                {/* edit details */}
                {activeForm === 'edit_oem' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {label('SR Number')}
                        <input className={inp} value={form.oem_sr_number || ''} onChange={e => set('oem_sr_number', e.target.value)} />
                      </div>
                      <div>
                        {label('RMA Number *')}
                        <input className={inp} value={form.oem_rma_number || ''} onChange={e => set('oem_rma_number', e.target.value)} />
                      </div>
                      <div>
                        {label('RMA Date *')}
                        <input type="date" className={inp} value={form.oem_rma_date || ''} onChange={e => set('oem_rma_date', e.target.value)} />
                      </div>
                    </div>
                    {editButtons}
                  </div>
                )}
              </StepCard>

              {/* Step 6 — Replacement Received */}
              <StepCard step={isFromOEM ? 4 : 6} title="Replacement Received" onEdit={rma.replacement_serial_number && activeForm !== 'edit_replacement' ? () => openEdit('edit_replacement') : undefined} done={stepIndex >= posOf('closed')} active={stepIndex === posOf('closed') - 1}
                actionLabel={stepIndex === posOf('closed') - 1 ? 'Enter Replacement Details' : null}
                onAction={() => openForm('closed')}>
                {stepIndex >= posOf('closed') && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field title="Replacement Serial" value={rma.replacement_serial_number} />
                    <Field title="Received Date" value={rma.replacement_received_date} />
                    <div className="col-span-2">
                      <p className="text-xs text-emerald-600 font-medium">Replacement added to spare warehouse. Faulty unit record removed.</p>
                    </div>
                  </div>
                )}
                {activeForm === 'closed' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {label('Replacement Serial Number *')}
                        <input className={inp} value={form.replacement_serial_number || ''} onChange={e => set('replacement_serial_number', e.target.value)} />
                      </div>
                      <div>
                        {label('Received Date *')}
                        <input type="date" className={inp} value={form.replacement_received_date || ''} onChange={e => set('replacement_received_date', e.target.value)} />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                      <button onClick={() => submit('mark-replacement-received')} disabled={saving}
                        className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                        {saving ? 'Saving…' : 'Confirm Replacement'}
                      </button>
                    </div>
                  </div>
                )}
                {/* edit details */}
                {activeForm === 'edit_replacement' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {label('Replacement Serial Number *')}
                        <input className={inp} value={form.replacement_serial_number || ''} onChange={e => set('replacement_serial_number', e.target.value)} />
                      </div>
                      <div>
                        {label('Received Date *')}
                        <input type="date" className={inp} value={form.replacement_received_date || ''} onChange={e => set('replacement_received_date', e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">The unit added to the spare warehouse is updated with the new serial number.</p>
                      </div>
                    </div>
                    {editButtons}
                  </div>
                )}
              </StepCard>

            </div>
          )}
        </>
      )}
    </div>
  );
}

function StepCard({ step, title, done, active, actionLabel, onAction, children, onEdit }) {
  return (
    <div className={`rounded-xl border p-4 transition-colors ${done ? 'border-emerald-200 bg-emerald-50/40' : active ? 'border-brand-200 bg-brand-50/30' : 'border-gray-100 bg-gray-50/50'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
            {done ? '✓' : step}
          </span>
          <span className={`text-sm font-semibold ${done ? 'text-emerald-700' : active ? 'text-brand-700' : 'text-gray-500'}`}>{title}</span>
        </div>
        {onEdit && (
          <button onClick={onEdit}
            className="px-3 py-1.5 text-xs rounded-lg bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            Edit
          </button>
        )}
        {actionLabel && onAction && (
          <button onClick={onAction}
            className="px-3 py-1.5 text-xs rounded-lg bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            {actionLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function DispatchForm({ form, set, spareUnits, challanRef, saving, onCancel, onSubmit }) {
  const inp2 = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300';
  const lbl = (t) => <p className="text-xs font-semibold text-brand-400 uppercase mb-1">{t}</p>;
  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          {lbl('Spare Unit *')}
          <select className={inp2} value={form.spare_unit || ''} onChange={e => set('spare_unit', e.target.value)}>
            <option value="">Select spare unit...</option>
            {spareUnits.map(u => (
              <option key={u.id} value={u.id}>
                {u._reservedForThis ? '★ Reserved for this ticket — ' : ''}{u.device_name} — {u.serial_number} ({u.warehouse})
              </option>
            ))}
          </select>
        </div>
        <div>
          {lbl('Dispatch Date *')}
          <input type="date" className={inp2} value={form.dispatch_date || ''} onChange={e => set('dispatch_date', e.target.value)} />
        </div>
        <div>
          {lbl('Courier Type *')}
          <select className={inp2} value={form.courier_type || ''} onChange={e => set('courier_type', e.target.value)}>
            <option value="">Select...</option>
            <option value="courier">Courier</option>
            <option value="hand">By Hand</option>
          </select>
        </div>
        {form.courier_type === 'courier' && (
          <>
            <div>
              {lbl('Courier Name')}
              <input className={inp2} value={form.courier_name || ''} onChange={e => set('courier_name', e.target.value)} placeholder="BlueDart, FedEx..." />
            </div>
            <div>
              {lbl('Tracking Number')}
              <input className={inp2} value={form.tracking_number || ''} onChange={e => set('tracking_number', e.target.value)} />
            </div>
          </>
        )}
        {form.courier_type === 'hand' && (
          <div>
            {lbl('Delivered By')}
            <input className={inp2} value={form.courier_name || ''} onChange={e => set('courier_name', e.target.value)} placeholder="Engineer / person name" />
          </div>
        )}
        <div>
          {lbl('Expected Delivery Date')}
          <input type="date" className={inp2} value={form.expected_delivery_date || ''} onChange={e => set('expected_delivery_date', e.target.value)} />
        </div>
        <div>
          {lbl('Delivery Challan')}
          <input type="file" ref={challanRef} accept=".pdf,.jpg,.jpeg,.png" className="text-sm text-gray-600" />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
        <button onClick={onSubmit} disabled={saving}
          className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Mark Dispatched'}
        </button>
      </div>
    </div>
  );
}

function FaultyPickupForm({ form, set, challanRef, saving, onCancel, onSubmit }) {
  const inp2 = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300';
  const lbl = (t) => <p className="text-xs font-semibold text-brand-400 uppercase mb-1">{t}</p>;
  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          {lbl('Pickup Date *')}
          <input type="date" className={inp2} value={form.faulty_pickup_date || ''} onChange={e => set('faulty_pickup_date', e.target.value)} />
        </div>
        <div>
          {lbl('Courier Type *')}
          <select className={inp2} value={form.return_courier_type || ''} onChange={e => set('return_courier_type', e.target.value)}>
            <option value="">Select...</option>
            <option value="courier">Courier</option>
            <option value="hand">By Hand</option>
          </select>
        </div>
        {form.return_courier_type === 'courier' && (
          <>
            <div>
              {lbl('Courier Name')}
              <input className={inp2} value={form.return_courier_name || ''} onChange={e => set('return_courier_name', e.target.value)} />
            </div>
            <div>
              {lbl('Tracking Number')}
              <input className={inp2} value={form.return_tracking_number || ''} onChange={e => set('return_tracking_number', e.target.value)} />
            </div>
          </>
        )}
        {form.return_courier_type === 'hand' && (
          <div>
            {lbl('Picked Up By')}
            <input className={inp2} value={form.return_courier_name || ''} onChange={e => set('return_courier_name', e.target.value)} />
          </div>
        )}
        <div>
          {lbl('Return Challan')}
          <input type="file" ref={challanRef} accept=".pdf,.jpg,.jpeg,.png" className="text-sm text-gray-600" />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
        <button onClick={onSubmit} disabled={saving}
          className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Mark Picked Up'}
        </button>
      </div>
    </div>
  );
}
